"use client";

import React from "react";
import { v3, inkAccent } from "@/lib/theme";
import { TOTALS, PORTFOLIO, CASHFLOW_SERIES, VALUE_SERIES, fmtEur, fmtPct } from "@/lib/data/mock";
import { Sparkline, Bars, Donut } from "@/components/ui/charts";
import { V3SectionHead } from "./V3SectionHead";
import { motion } from "framer-motion";

function V3KpiCard({ label, value, delta, sub }: any) {
  return (
    <div style={{ padding: "26px 28px" }}>
      <div style={{ font: `12px ${v3.sans}`, color: v3.dim }}>{label}</div>
      <div style={{ font: `500 36px ${v3.sans}`, color: v3.fg, marginTop: 8, letterSpacing: "-0.025em" }}>{value}</div>
      {delta && <div style={{ font: `12px ${v3.mono}`, color: inkAccent(), marginTop: 4 }}>{delta}</div>}
      {sub && <div style={{ font: `11px ${v3.sans}`, color: v3.dim2, marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

function V3OvTabA() {
  const ink = inkAccent();
  return (
    <div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", borderBottom: `1px solid ${v3.line}` }}>
        <V3KpiCard label="Portfoliowert"    value={fmtEur(TOTALS.wert)}                                  delta={fmtPct(TOTALS.wertstgPct) + " ggü. Kauf"} />
        <V3KpiCard label="Rendite (Ø)"      value={`${TOTALS.rendite.toFixed(2).replace(".", ",")} %`}  sub="brutto, Gesamtportfolio" />
        <V3KpiCard label="Cashflow / Monat" value={fmtEur(TOTALS.cf, { sign: true })}                   sub="nach Tilgung & Rücklagen" />
        <V3KpiCard label="Miete p.a."       value={fmtEur(TOTALS.miete * 12)}                           sub="Bruttoeinnahmen · 12 Mieter" />
      </div>
      <div className="rp-stack" style={{ display: "grid", gridTemplateColumns: "1.7fr 1fr" }}>
        <div style={{ padding: 28, borderRight: `1px solid ${v3.line}` }}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 18, flexWrap: "wrap", gap: 12 }}>
            <div style={{ font: `15px ${v3.sans}`, color: v3.fg, fontWeight: 500 }}>Portfoliowert · 24 Monate</div>
            <div style={{ display: "flex", gap: 6 }}>
              {["1M", "3M", "12M", "24M", "ALL"].map((p, i) => {
                const active = i === 3;
                return (
                  <span key={i} style={{ padding: "4px 10px", borderRadius: 999, font: `11px ${v3.mono}`, color: active ? v3.accent : v3.dim, background: active ? v3.accentBg : "transparent", border: active ? `1px solid ${v3.accentDim}` : "1px solid transparent" }}>{p}</span>
                );
              })}
            </div>
          </div>
          <div style={{ color: ink }}>
            <Sparkline data={VALUE_SERIES} height={220} width={780} stroke={ink} fill={v3.intense ? v3.accentBg : "rgba(250,250,250,0.04)"} strokeWidth={1.5} dots />
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", marginTop: 10, font: `11px ${v3.mono}`, color: v3.dim2 }}>
            <span>Apr 24</span><span>Okt 24</span><span>Apr 25</span><span>Okt 25</span><span>Heute</span>
          </div>
        </div>
        <div style={{ padding: 28 }}>
          <div style={{ font: `15px ${v3.sans}`, color: v3.fg, fontWeight: 500, marginBottom: 18 }}>Allokation</div>
          <div style={{ display: "flex", alignItems: "center", gap: 24, flexWrap: "wrap" }}>
            <div>
              <Donut size={130} thickness={16} gap={5} trackColor={v3.line2}
                slices={[
                  { value: 4, color: v3.accent },
                  { value: 1, color: v3.fg2 },
                  { value: 1, color: v3.dim },
                ]} />
            </div>
            <div style={{ flex: 1 }}>
              {[["Wien", "67 %", v3.accent], ["Linz", "21 %", v3.fg2], ["Graz", "12 %", v3.dim]].map((r, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 0", borderTop: i > 0 ? `1px solid ${v3.line}` : 0, font: `13px ${v3.sans}`, color: v3.fg2 }}>
                  <span style={{ width: 6, height: 6, borderRadius: 99, background: r[2] as string }} />
                  <span style={{ flex: 1 }}>{r[0]}</span>
                  <span style={{ font: `13px ${v3.mono}` }}>{r[1]}</span>
                </div>
              ))}
            </div>
          </div>
          <div style={{ marginTop: 28, padding: 18, background: v3.intense ? v3.accentBg : v3.surf, border: `1px solid ${v3.intense ? v3.accentDim : v3.line}`, borderRadius: 12 }}>
            <div style={{ font: `11px ${v3.sans}`, color: v3.accent, textTransform: "uppercase", letterSpacing: "0.08em", display: "flex", alignItems: "center", gap: 6 }}>
              <span className="v3-pulse animate-pulse" style={{ width: 6, height: 6, borderRadius: 99, background: v3.accent }} /> Nächste Aktion
            </div>
            <div style={{ font: `15px ${v3.sans}`, color: v3.fg, marginTop: 8, fontWeight: 500 }}>Objekt 01 · Mietanpassung VPI</div>
            <div style={{ font: `12px ${v3.sans}`, color: v3.dim, marginTop: 4 }}>+€ 34 / Monat möglich ab 01.05.2026</div>
          </div>
        </div>
      </div>
    </div>
  );
}

function V3OvTabB() {
  const ink = inkAccent();
  return (
    <div className="overflow-x-auto">
      <div style={{ minWidth: 800 }}>
        <div style={{ padding: "16px 28px", display: "grid", gridTemplateColumns: "32px 1.4fr 1fr 0.9fr 0.9fr 0.9fr 0.7fr 80px", gap: 12, font: `11px ${v3.sans}`, color: v3.dim, borderBottom: `1px solid ${v3.line}`, textTransform: "uppercase", letterSpacing: "0.06em" }}>
          <span>#</span><span>Objekt</span><span>Typ</span><span style={{ textAlign: "right" }}>Kauf</span><span style={{ textAlign: "right" }}>Wert</span><span style={{ textAlign: "right" }}>Miete</span><span style={{ textAlign: "right" }}>Rend.</span><span style={{ textAlign: "right" }}>CF</span>
        </div>
        {PORTFOLIO.map((o: any, i: number) => (
          <div key={o.id} style={{ padding: "16px 28px", display: "grid", gridTemplateColumns: "32px 1.4fr 1fr 0.9fr 0.9fr 0.9fr 0.7fr 80px", gap: 12, alignItems: "center", borderBottom: i < PORTFOLIO.length - 1 ? `1px solid ${v3.line}` : 0 }}>
            <span style={{ font: `12px ${v3.mono}`, color: v3.dim2 }}>{String(o.id).padStart(2, "0")}</span>
            <span style={{ font: `14px ${v3.sans}`, color: v3.fg, fontWeight: 500 }}>{o.city}</span>
            <span style={{ font: `12px ${v3.mono}`, color: v3.dim }}>{o.typ}</span>
            <span style={{ textAlign: "right", font: `13px ${v3.mono}`, color: v3.dim }}>{fmtEur(o.kaufpreis / 1000)}K</span>
            <span style={{ textAlign: "right", font: `13px ${v3.mono}`, color: v3.fg2 }}>{fmtEur(o.mwert / 1000)}K</span>
            <span style={{ textAlign: "right", font: `13px ${v3.mono}`, color: v3.fg2 }}>{fmtEur(o.miete)}</span>
            <span style={{ textAlign: "right", font: `13px ${v3.mono}`, color: v3.fg2 }}>{o.rendite.toFixed(2).replace(".", ",")} %</span>
            <span style={{ textAlign: "right", font: `13px ${v3.mono}`, color: o.cashflow >= 0 ? ink : v3.dim }}>
              {o.cashflow >= 0 ? "+" : "−"}{Math.abs(o.cashflow)} €
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function V3OvTabC() {
  const ink = inkAccent();
  return (
    <div style={{ padding: 28 }} className="overflow-x-auto">
      <div style={{ minWidth: 600 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
          <div style={{ font: `15px ${v3.sans}`, color: v3.fg, fontWeight: 500 }}>Cashflow · Monatsbasis · 24M</div>
          <div style={{ font: `12px ${v3.mono}`, color: v3.dim }}>EUR / mtl.</div>
        </div>
        <div style={{ marginTop: 24, color: ink }}>
          <Bars data={CASHFLOW_SERIES} height={240} width={1180} color={ink} />
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", marginTop: 12, font: `11px ${v3.mono}`, color: v3.dim2 }}>
          <span>Apr 24</span><span>Okt 24</span><span>Apr 25</span><span>Okt 25</span><span>Heute</span>
        </div>
      </div>
    </div>
  );
}

function V3OvTabD() {
  const ink = inkAccent();
  return (
    <div style={{ padding: 28, display: "grid", gridTemplateColumns: "1.4fr 1fr", gap: 32 }} className="rp-stack">
      <div>
        <div style={{ font: `15px ${v3.sans}`, color: v3.fg, fontWeight: 500, marginBottom: 18 }}>Marktwert · vs. Kaufpreis</div>
        <div style={{ color: ink }}>
          <Sparkline data={VALUE_SERIES} height={260} width={700} stroke={ink} fill={v3.intense ? v3.accentBg : "rgba(250,250,250,0.04)"} strokeWidth={1.6} />
        </div>
      </div>
      <div>
        <div style={{ font: `15px ${v3.sans}`, color: v3.fg, fontWeight: 500, marginBottom: 18 }}>Wertsteigerung pro Bezirk</div>
        {[
          ["Wien 1070 · Altbau", "+24,3 %"],
          ["Graz · West",        "+14,3 %"],
          ["Wien 1020 · Neubau", "+12,8 %"],
          ["Linz · Zentrum",     "+12,6 %"],
          ["Wien 1100 · Neubau", "+11,5 %"],
          ["Wien 1160 · Neubau", "+11,2 %"],
        ].map((r, i) => (
          <div key={i} style={{ display: "grid", gridTemplateColumns: "1fr 80px", padding: "13px 0", borderTop: `1px solid ${v3.line}`, alignItems: "center" }}>
            <span style={{ font: `14px ${v3.sans}`, color: v3.fg }}>{r[0]}</span>
            <span style={{ textAlign: "right", font: `13px ${v3.mono}`, color: ink }}>{r[1]}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export function V3SectionOverview() {
  const [tab, setTab] = React.useState(0);
  const tabs = ["Übersicht", "Objekte", "Cashflow", "Markt"];

  return (
    <motion.section 
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-100px" }}
      transition={{ duration: 0.8, ease: "easeOut" }}
      style={{ padding: "120px 56px", background: v3.surf2, borderTop: `1px solid ${v3.line}`, borderBottom: `1px solid ${v3.line}` }} className="rp-section"
    >
      <V3SectionHead n={1} kicker="Der Überblick den du verdienst"
        title="Dein Portfolio."
        dimWord="Auf einen Blick."
        sub="Klicke durch die Tabs. Es ist alles echt — anonymisierte Daten aus einem Beispielportfolio." />

      <div style={{ background: v3.bg, border: `1px solid ${v3.line2}`, borderRadius: 18, overflow: "hidden" }}>
        <div style={{ display: "flex", borderBottom: `1px solid ${v3.line}`, padding: "8px 12px", gap: 4, alignItems: "center", overflowX: "auto" }}>
          {tabs.map((t, i) => (
            <button key={i} onClick={() => setTab(i)} style={{
              padding: "8px 16px", borderRadius: 999,
              background: i === tab ? v3.accentBg : "transparent",
              color: i === tab ? v3.accent : v3.dim,
              border: i === tab ? `1px solid ${v3.accentDim}` : "1px solid transparent",
              font: `500 13px ${v3.sans}`, cursor: "pointer",
            }}>{t}</button>
          ))}
          <div style={{ flex: 1, textAlign: "right", padding: "8px 12px", font: `11px ${v3.mono}`, color: v3.dim, minWidth: 150 }}>
            Anna G. · 6 Objekte
          </div>
        </div>
        {tab === 0 && <V3OvTabA />}
        {tab === 1 && <V3OvTabB />}
        {tab === 2 && <V3OvTabC />}
        {tab === 3 && <V3OvTabD />}
      </div>
    </motion.section>
  );
}
